export const meta = {
  id: '51-finale-halloween',
  order: 51,
  titleFi: 'Finaali: Halloween Targaryeneina',
  titleEn: 'Finale: Halloween as Targaryens',
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: ['gūrēnilaksir', 'āhibnītsos', 'sīmonnon', 'prānon', 'udrir', 'udir', 'rakella', 'kelinītsos', 'māzīlarion', 'ōz', 'jenys', 'rōvēgrī', 'ours:elentys', 'ours:prattys', 'ours:nārāpa', 'ours:mīvarrirys', 'ours:henujnon'],
  exampleIds: ['ex-9c18d9e68e8f', 'got-s3e04-008', 'ex-faaf3a536f07', 'got-s3e07-003', 'got-s7e02-004', 'ex-b95f4e0bde15', 'ex-c6aad7496116', 'ex-1c6ea66c8e1e', 'ex-f79912f267c1', 'got-s3e06-001', 'got-s3e06-002', 'got-s4e05-004', 'ex-891ef0b7ac8e', 'ex-e61df45a8982', 'hotd-s1e07-014', 'ex-12bff7aa2c7d', 'ex-fa2a87cec2d2', 'hotd-s1e01-004'],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_numerals',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Derivational_Affixes',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Corpus',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  showLines: ['got-s3e04-008', 'got-s3e07-003', 'got-s7e02-004', 'got-s3e06-001', 'got-s3e06-002', 'got-s4e05-004', 'hotd-s1e07-014', 'hotd-s1e01-004'],
  pronunciation: { focus: 'rhythm', words: ['rytsas', 'kirimvose', 'drakarys'], lines: ['got-s3e07-003', 'got-s4e05-004', 'got-s3e06-001', 'got-s3e06-002', 'got-s3e04-008'] },
  phrases: ['rytsas', 'avy-rytsuran', 'ōñoso', 'jiōrna', 'kesīr-drējī-jiōrilāks', 'kirimvose', 'valar-morghūlis', 'valar-dohaeris', 'geros-ilas', 'adervose', 'biarvī-aōt'],
  letter: {
    fi: 'Kirjoita Halloween-iltasi repliikit: tervehdys, esittely ja lähtösanat.',
    en: 'Write your lines for Halloween night: greeting, introduction and farewell.',
  },
  scene: 'hotd-s1e07-1',
  // the Halloween script (docs/COURSE.md "The Halloween script")
  halloween: ['esitys', 'stage'],
  // Halloween-mittari (docs/COURSE.md): the meter's sentence on this week's hub
  halloweenMeter: true,
}

# Finaali: Halloween Targaryeneina

Halloweeniin, sunnuntaihin 31.10.2027, on enää muutama viikko. Aloititte lokakuussa 2026 äänteistä ja painosta, ja nyt luette Daenerysin puheita ja Rhaenyran riitoja ja kirjoitatte toisillenne kirjeitä. Tällä viimeisellä oppitunnilla valmistaudutte iltaan, jona olette Targaryeneja: esittäytyminen, tervehdykset, valat ja hyvästit, ja kaikki ääneen Petersonin malliin. Lopuksi katsotaan koko matkaa taaksepäin ja mietitään, miten tästä jatketaan.

<Example id="ex-9c18d9e68e8f" />

Tässä on ensimmäisen oppitunnin otsikko valyriaksi, ja nyt osaatte lukea sen: <Hv glossed>ēlie</Hv> ’ensimmäinen’ (<LessonLink n={48}>48</LessonLink>) on jälkiasemassa (<LessonLink n={47}>47</LessonLink>) ja maa- ja vesisuvun yhteisessä muodossa <Hv glossed l="ēlie">Ēlior</Hv>, koska <Hv glossed>gūrēnilaksir</Hv> ’oppitunti’ on vesisukuinen.

## Halloween-ilta: esittäytyminen

Targaryen esittäytyy nimellä, suvulla ja verellä. Daenerysin esittely Astaporissa on koko kurssin tunnetuin malli:

<Line id="got-s3e04-008" />

Hovissa esittelyn hoitaa kuuluttaja, joka luettelee nimen, suvun ja arvonimet:

<Example id="ex-faaf3a536f07" />

Vaihtakaa malliin oman hahmonne nimi. Nimi pysyy nominatiivissa, ja muu lause on Daenerysin oma:

<Practice hv="Nyke Daemon hen Targārio Lentrot, hen Valyrio Uēpo ānogār iksan." fi="Minä olen Daemon Targaryenin suvusta, Vanhan Valyrian verta." />

Lyhyemmät muodot ja arvonimet löytyvät oppitunneilta <LessonLink n={49}>49</LessonLink> ja <LessonLink n={50}>50</LessonLink>. Käyttäkää vain sanakirjan ja repliikkien arvonimiä, älkää keksikö uusia.

## Tervehdykset ja hyvästit

<Line id="got-s3e07-003" />

<Line id="got-s7e02-004" />

Sanakirjan tervehdyksiä illan alkuun:

- <Hv glossed>rytsas</Hv> – hei, terve
- <Hv glossed>ōñoso</Hv> – valon nimeen (tervehdys)
- <Hv fi="tervehdin sinua">avy rytsuran</Hv>
- <Hv glossed>jiōrna</Hv> – tervetuloa
- <Hv glossed>kirimvose</Hv> – kiitos

Ja illan loppuun:

- <Hv fi="näkemiin, hyvästi">geros ilas</Hv>
- <Hv glossed>adervose</Hv> – näkemiin, hyvästi (kirjaimellisesti ’nopeudella’)
- <Hv fi="onnea sinulle">biarvī aōt</Hv>

Ja tietysti kahden valyrialaisen tervehdys ja vastaus:

<Line id="got-s3e06-001" />

<Line id="got-s3e06-002" />

## Valat ja tunnuslauseet

Targaryenien tunnuslause:

<Example id="ex-b95f4e0bde15" />

Lupaus, jonka Daenerys antaa Tahrattomille, sopii myös toiselle annettavaksi:

<Example id="ex-c6aad7496116" />

Yhdelle ihmiselle sanotaan <Hv glossed>aōt</Hv> ’sinulle’ (<LessonLink n={49}>oppitunti 49</LessonLink>):

<Practice hv="Aōt kīvio ñuhe tepan." fi="Annan sinulle sanani." />

Kaksi juhlavaa lausetta tulesta ja valosta:

<Example id="ex-1c6ea66c8e1e" />

<Example id="ex-f79912f267c1" />

Rhaenyran kosinta Daemonille (<LessonLink n={50}>oppitunti 50</LessonLink>) on pariskunnan oma vala.

## Ääntämisharjoitus: Peterson mallina

Harjoitelkaa illan repliikit Petersonin äänitteitä vasten. Kuunnelkaa hidastettuna ja silmukassa, nauhoittakaa oma versionne Tämä viikko -näkymän Puhu-kohdassa ja verratkaa painoa ja vokaalien pituutta. Petersonin äänite on tervehdyksistä <Hv l="rytsas">rytsas</Hv> ja <Hv l="kirimvose">kirimvose</Hv>, valyrialaisten tervehdyksestä ja vastauksesta sekä Daenerysin esittelystä ja lupauksesta.

<Line id="got-s4e05-004" />

<Pronounce l="kirimvose" word="kirimvose" />

Sarjan näyttelijöiden äänitteet ovat kuuntelumateriaalia, eivät ääntämisen malli. House of the Dragonin repliikeille Petersonin äänitettä ei ole, joten niissä nojatkaa painosääntöön (<LessonLink n={1}>oppitunti 1</LessonLink>).

## Kurssin kartta

**Perusta (<LessonLink n={1}>1</LessonLink>–<LessonLink n={6}>6</LessonLink>).** Äänteet ja paino (<LessonLink n={1}>1</LessonLink>), ensimmäiset lauseet (<LessonLink n={2}>2</LessonLink>), olla-verbi (<LessonLink n={3}>3</LessonLink>), fraasit (<LessonLink n={4}>4</LessonLink>), sijojen ja verbinpäätteiden idea (<LessonLink n={5}>5</LessonLink>) sekä osoittavat pronominit (<LessonLink n={6}>6</LessonLink>).

**Substantiivin maailma (<LessonLink n={7}>7</LessonLink>–<LessonLink n={11}>11</LessonLink>).** Neljä sukua: johdatus (<LessonLink n={7}>7</LessonLink>), kuusuku ja aurinkosuku (<LessonLink n={8}>8</LessonLink>), vesisuku ja maasuku (<LessonLink n={9}>9</LessonLink>). Genetiivi (<LessonLink n={10}>10</LessonLink>) sekä omistussanat ja perhe (<LessonLink n={11}>11</LessonLink>).

**Lause rakentuu (<LessonLink n={12}>12</LessonLink>–<LessonLink n={20}>20</LessonLink>).** Kysymykset (<LessonLink n={12}>12</LessonLink>), eläimet (<LessonLink n={13}>13</LessonLink>), preesens (<LessonLink n={14}>14</LessonLink>), koot ja ruoka (<LessonLink n={15}>15</LessonLink>), datiivi (<LessonLink n={16}>16</LessonLink>), kielto ja subjunktiivi (<LessonLink n={17}>17</LessonLink>), persoonapronominit (<LessonLink n={18}>18</LessonLink>), lokatiivi (<LessonLink n={19}>19</LessonLink>), fraasit, värit ja lainasanat (<LessonLink n={20}>20</LessonLink>).

**Lisää sijoja ja aikoja (<LessonLink n={21}>21</LessonLink>–<LessonLink n={27}>27</LessonLink>).** Vokatiivi ja käskyt (<LessonLink n={21}>21</LessonLink>), perfekti (<LessonLink n={22}>22</LessonLink>), instrumentaali ja komitatiivi (<LessonLink n={23}>23</LessonLink>), ihmiskeho ja adverbit (<LessonLink n={24}>24</LessonLink>), luvut (<LessonLink n={25}>25</LessonLink>), futuuri (<LessonLink n={26}>26</LessonLink>) ja infinitiivirakenteet (<LessonLink n={27}>27</LessonLink>).

**Neljä lukua ja valenssi (<LessonLink n={28}>28</LessonLink>–<LessonLink n={37}>37</LessonLink>).** Muutama ja kollektiivi (<LessonLink n={28}>28</LessonLink>, <LessonLink n={31}>31</LessonLink>, <LessonLink n={35}>35</LessonLink>), lainanimet (<LessonLink n={29}>29</LessonLink>), passiivi (<LessonLink n={30}>30</LessonLink>), luonto, vaatteet ja määrittävät lausekkeet (<LessonLink n={31}>31</LessonLink>), subjunktiivi sivulauseessa (<LessonLink n={32}>32</LessonLink>), applikatiivit (<LessonLink n={33}>33</LessonLink>), imperfekti (<LessonLink n={34}>34</LessonLink>), lohikäärmeet (<LessonLink n={36}>36</LessonLink>) sekä refleksiiviset ja emfaattiset pronominit ja rakentaminen (<LessonLink n={37}>37</LessonLink>).

**Verbijärjestelmä kokonaisuutena (<LessonLink n={38}>38</LessonLink>–<LessonLink n={45}>45</LessonLink>).** Aoristi (<LessonLink n={38}>38</LessonLink>), subjunktiivi sivulauseissa (<LessonLink n={39}>39</LessonLink>), partisiipit (<LessonLink n={40}>40</LessonLink>), pluskvamperfekti, mennyt toistuva ja kirjeet (<LessonLink n={41}>41</LessonLink>), infinitiivit, käskyt ja toivotukset (<LessonLink n={42}>42</LessonLink>), <Term id="luokka">verbiluokat</Term> ja epäsäännölliset verbit (<LessonLink n={43}>43</LessonLink>), passiivi (<LessonLink n={44}>44</LessonLink>) sekä verbin etuliitteet ja johtimet (<LessonLink n={45}>45</LessonLink>).

**Lause ja sana (<LessonLink n={46}>46</LessonLink>–<LessonLink n={48}>48</LessonLink>).** Relatiivilauseet ja sidesanat (<LessonLink n={46}>46</LessonLink>), sanajärjestys, kysymykset ja rinnastus (<LessonLink n={47}>47</LessonLink>) sekä vertailu, sananmuodostus ja luvut (<LessonLink n={48}>48</LessonLink>).

**Targaryenien kieli (<LessonLink n={49}>49</LessonLink>–<LessonLink n={51}>51</LessonLink>).** Daenerys Astaporissa (<LessonLink n={49}>49</LessonLink>), Rhaenyra ja Daemon (<LessonLink n={50}>50</LessonLink>) ja Halloween-ilta (tämä oppitunti).

## Pikatesti: tunnistatko nämä?

Käykää lista läpi kahdestaan. Jos jokin ei aukea heti, palatkaa suluissa mainittuun oppituntiin. Se on kertausta, ei epäonnistumista.

1. <Hv glossed>zaldrīzose</Hv>: mikä sija ja mihin kahteen tehtävään se sopii? (<LessonLink n={23}>23</LessonLink>, <LessonLink n={48}>48</LessonLink>)
2. <Hv glossed>ābrā</Hv>: kaksi mahdollista tulkintaa. (<LessonLink n={19}>19</LessonLink>, <LessonLink n={47}>47</LessonLink>)
3. <Hv fi="ei ole">iksos daor</Hv>: miksi ei <Hv>issa daor</Hv>? (<LessonLink n={17}>17</LessonLink>)
4. <Hv glossed>valun</Hv> ja <Hv l="vala" glossed>valar</Hv>: kumpi saa verbin monikkoon? (<LessonLink n={28}>28</LessonLink>, <LessonLink n={35}>35</LessonLink>)
5. <Hv glossed>ūndetan</Hv> ja <Hv glossed>ōdrikilza</Hv>: aikamuoto ja persoona. (<LessonLink n={22}>22</LessonLink>, <LessonLink n={26}>26</LessonLink>)
6. <Hv fi="miehen, joka …">lue vale</Hv>: mistä akkusatiivi tulee? (<LessonLink n={46}>46</LessonLink>)
7. <Hv glossed>Lo … sepār</Hv>: mitä se tarkoittaa ja milloin ehtolauseessa on subjunktiivi? (<LessonLink n={39}>39</LessonLink>, <LessonLink n={46}>46</LessonLink>)
8. <Hv glossed>rōvykta</Hv>: mikä vertailuaste ja missä sijassa on vertailukohta? (<LessonLink n={48}>48</LessonLink>)
9. <Hv>Dovaogēdy</Hv>, <Hv>laehurloqitta</Hv>, <Hv>riñītsos</Hv>: mistä paloista ne koostuvat? (<LessonLink n={48}>48</LessonLink>)
10. <Hv l="jeme" glossed>jemot</Hv> ja <Hv glossed>sytivīlībilāt</Hv>: pronominin sija ja verbin etuliitteet. (<LessonLink n={18}>18</LessonLink>, <LessonLink n={33}>33</LessonLink>, <LessonLink n={49}>49</LessonLink>)

## Loppukoe: hääruno

House of the Dragonin ensimmäisen kauden seitsemännessä jaksossa Rhaenyra ja Daemon vihitään, ja pappi lausuu runon. Runokieli on tiivistä: monesta säkeestä puuttuu verbi, ja sanajärjestys palvelee rytmiä. Lue runo ja katso, kuinka paljon tunnistat.

<Line id="hotd-s1e07-014" />

Muutama vihje (kaikki asiat ovat tuttuja tältä kurssilta):

- <Hv glossed>Hen lantoti ānogar</Hv>: *kahden veri*, sanatarkasti *kahdesta veri*. <Hv>Hen</Hv> + lokatiivi (<LessonLink n={49}>49</LessonLink>), ja lukusana on yksinään ilman substantiivia. Monikossa genetiivi, datiivi ja lokatiivi näyttävät samalta (<Hv l="lanta">lantoti</Hv>).
- <Hv glossed>Izulī ampā perzī</Hv>: *neljässätoista tulessa* (lokatiivi), valyrialaisten Neljässätoista liekissä. Luku kymmenen on pidennetyssä rinnastusmuodossa <Hv>ampā</Hv> (<LessonLink n={25}>25</LessonLink>, <LessonLink n={47}>47</LessonLink>).
- <Hv glossed>Prūmī lanti … sēteksi</Hv>: *kaksi sydäntä taotaan*, passiivin preesens (<LessonLink n={30}>30</LessonLink>).
- <Hv glossed>Qēlossa ozūndesi</Hv>: *tähdet todistavat*.
- <Hv glossed>Sȳndroro ōñō jēdo</Hv>: *pimeyden ja valon ajan*. <Hv l="ōños" glossed>Ōñō</Hv> on pidennetty genetiivi <Hv glossed>ōño</Hv> sanasta <Hv glossed>ōños</Hv> ’valo’ (<LessonLink n={47}>47</LessonLink>).
- <Hv glossed>… jēdo / Rȳ kīvia mazvestraksi</Hv>: *ajan läpi lausutaan valat*. Postpositio <Hv>rȳ</Hv> tulee genetiivin <Hv fi="ajan">jēdo</Hv> jälkeen, vaikka säe katkeaa niiden välissä (<LessonLink n={49}>49</LessonLink>), ja <Hv fi="lausutaan">mazvestraksi</Hv> on passiivi.

Kun luet runon nyt uudestaan, huomaat, ettei se ole enää salakieltä.

## Lavalla

Juhlailtana avatkaa Lavalla-näkymä. Se näyttää koko käsikirjoituksen järjestyksessä tummalla pohjalla ja isolla tekstillä, ja se toimii ilman verkkoyhteyttä. Omat repliikkisi on korostettu, ja niiden valyria on piilossa: napauta repliikkiä, niin näet sen valyriaksi. Toisen repliikit näkyvät kokonaan, ja edellinen repliikki on vihjeesi. Kokeilkaa näkymää tällä viikolla kerran alusta loppuun, niin tiedätte, miltä ilta tuntuu.

<HalloweenScript />

## Miten tästä jatketaan

Daemon sanoo Rhaenyralle hetkellä, jolla on väliä:

<Example id="ex-891ef0b7ac8e" />

Ja toisessa kohdassa:

<Example id="ex-12bff7aa2c7d" />

Tämä on vasta alku. Tässä on viisi tapaa pitää kieli elossa.

1. **Kortit joka päivä.** Tämän oppitunnin viikolla kurssi avaa viimeisetkin sanansa, joten kaikki kurssin sanat, sekä Petersonin että omat, ovat korteissa. Uusia ei enää tule, mutta kertaus jatkuu, ja vapaassa tilassa harjoitellut kortit päivittävät samaa kertausaikataulua.
2. **Lukeminen.** Sovelluksessa on kaikki sarjojen valyriankieliset repliikit ja yli tuhat sanakirjan esimerkkilausetta. Ottakaa joka viikko yksi kohtaus ja lukekaa se kuten oppitunneilla <LessonLink n={49}>49</LessonLink>–<LessonLink n={50}>50</LessonLink>: ääni, sanasta sanaan -käännös ja lopuksi koko kohtaus putkeen.
3. **Kirjeet.** Yksi kirje viikossa riittää. Käyttäkää <LessonLink n={41}>oppitunnin 41</LessonLink> rakennetta ja merkitkää epävarmat kohdat. Ne käydään läpi yhteisellä tarkistuskierroksella.
4. **Puhuminen.** Viikoittaisesta yhteisestä hetkestänne voi varata kymmenen minuuttia valyrialle: kysymyksiä (<LessonLink n={47}>47</LessonLink>), kuulumisia (<LessonLink n={41}>41</LessonLink>) ja yksi kohtaus ääneen rooleittain.
5. **Uudet sanat.** Petersonin wiki elää, ja kun sanakirja päivittyy, uudet sanat merkitään uusiksi. Kun tarvitsette sanaa, jota ei ole olemassa, keksikää oma. Johtimet (<LessonLink n={48}>48</LessonLink>) ja yhdyssanat ovat juuri tätä varten, ja sovellus merkitsee tällaisen sanan aina teidän keksimäksenne.

<Example id="ex-e61df45a8982" />

Ei ole valtakuntaa ilman kieltä – ja nyt teidänkin valtakunnallanne on omansa.

<Example id="ex-fa2a87cec2d2" />

<Line id="hotd-s1e01-004" />

<Vocab />

<Check />
